import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cake, Users, Gift, Briefcase, Heart, GraduationCap,
  Waves, Umbrella, Flame, Gamepad2, Car, PartyPopper,
  CheckCircle2, Send, Loader2, MessageCircle, X,
} from "lucide-react";
import { CONTACT } from "../mock";
import { useMedia, resolveUrl } from "../context/MediaContext";
import { useToast } from "../hooks/use-toast";
import PageHero from "../components/PageHero";
import { WhatsApp } from "../components/Social";
import { Reveal, Magnetic, EASE } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  { icon: Cake, label: "Birthday Parties" },
  { icon: Users, label: "Family Gatherings" },
  { icon: Gift, label: "Baby & Bridal Showers" },
  { icon: Briefcase, label: "Workshops & Retreats" },
  { icon: Heart, label: "Intimate Celebrations" },
  { icon: GraduationCap, label: "Graduations" },
];

const HIGHLIGHTS = [
  { icon: Waves, label: "Private pool, spa & tanning ledge" },
  { icon: Umbrella, label: "2 pergolas & patio dining" },
  { icon: Flame, label: "Grill & outdoor mini golf" },
  { icon: Gamepad2, label: "Poker table & arcade machine" },
  { icon: Users, label: "Comfortably hosts your guests" },
  { icon: Car, label: "Free private parking" },
];

const TYPE_OPTIONS = [
  "Birthday Party", "Family Gathering", "Baby / Bridal Shower",
  "Workshop / Retreat", "Intimate Celebration", "Graduation", "Other",
];

export default function EventsPage() {
  const { toast } = useToast();
  const { slots, eventGallery } = useMedia();
  const EVENT_PHOTOS = eventGallery;
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    event_type: "Birthday Party", event_date: "", guests: "20", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const inputCls =
    "w-full bg-white/70 border border-[#0A3B2E]/10 rounded-xl px-4 py-3 text-[#16241E] placeholder:text-[#16241E]/40 focus:outline-none focus:ring-2 focus:ring-[var(--moss)]/40 focus:border-[var(--moss)] transition";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Add your details", description: "Name and email are required." });
      return;
    }
    if (!form.phone || form.phone.replace(/[^0-9]/g, "").length < 7) {
      toast({ title: "Phone number required", description: "Please enter a valid phone number so we can reach you." });
      return;
    }
    if (!form.event_date) {
      toast({ title: "Event date required", description: "Please select a date for your event." });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/event-inquiries`, {
        name: form.name, email: form.email, phone: form.phone,
        event_type: form.event_type, event_date: form.event_date,
        guests: Number(form.guests) || 0, message: form.message,
      });
      setDone(data);
      toast({ title: "Inquiry sent!", description: "We'll reach out with availability & pricing soon." });
      setForm({ name: "", email: "", phone: "", event_type: "Birthday Party", event_date: "", guests: "20", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Couldn't send inquiry", description: typeof msg === "string" ? msg : "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F7F2E9] min-h-screen" data-testid="events-page">
      <PageHero
        eyebrow="Celebrate With Us"
        title="Host Your"
        accent="Event"
        image={resolveUrl(slots.cardIndoor)}
        subtitle="From birthdays to bridal showers, The Jackson House is the perfect private venue for your next unforgettable gathering."
      />

      {/* Event types */}
      <section className="max-w-[1200px] mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <Reveal>
            <span className="inline-block font-body uppercase tracking-[0.35em] text-xs text-[var(--moss-deep)] mb-5">
              Perfect For
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black text-[#0A3B2E] text-4xl md:text-6xl leading-none">
              Every <span className="italic gradient-text font-light">Occasion</span>
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {EVENT_TYPES.map((t, i) => (
            <Reveal key={t.label} delay={0.05 * i}>
              <div
                data-testid={`event-type-${i}`}
                className="group flex items-center gap-4 rounded-[22px] bg-white/60 border border-white/70 p-5 h-full shadow-[0_15px_40px_rgba(10,59,46,0.06)] hover:-translate-y-1 transition-transform"
              >
                <span className="w-12 h-12 rounded-full sunset-fill grid place-items-center text-white shrink-0">
                  <t.icon size={22} />
                </span>
                <span className="font-body font-semibold text-[#0A3B2E]">{t.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-[1300px] mx-auto px-6 pb-8" data-testid="events-gallery">
        <div className="text-center mb-14">
          <Reveal>
            <span className="inline-block font-body uppercase tracking-[0.35em] text-xs text-[var(--moss-deep)] mb-5">
              The Memories
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black text-[#0A3B2E] text-4xl md:text-6xl leading-none">
              Celebrations <span className="italic gradient-text font-light">Here</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="text-[#16241E]/60 max-w-xl mx-auto mt-6 text-lg font-light">
              Real parties, showers &amp; get-togethers hosted at The Jackson House.
            </p>
          </Reveal>
        </div>
        <div className="[column-count:2] md:[column-count:3] lg:[column-count:4] [column-gap:1rem] md:[column-gap:1.25rem]">
          {EVENT_PHOTOS.map((src, i) => (
            <motion.button
              key={src}
              type="button"
              onClick={() => setLightbox(i)}
              data-testid={`events-gallery-img-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 4) * 0.05 }}
              className="group relative mb-4 md:mb-5 block w-full break-inside-avoid overflow-hidden rounded-[20px] shadow-[0_15px_40px_rgba(10,59,46,0.1)]"
            >
              <img
                src={resolveUrl(src)}
                alt={`Event at The Jackson House ${i + 1}`}
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.06]"
              />
              <span className="absolute inset-0 bg-[#06271D]/0 group-hover:bg-[#06271D]/25 transition-colors duration-500" />
            </motion.button>
          ))}
        </div>
      </section>

      {/* Highlights + form */}
      <section className="max-w-[1240px] mx-auto px-5 sm:px-6 pb-24 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12">
        {/* Left: why host here */}
        <div className="lg:col-span-2">
          <Reveal>
            <div className="relative rounded-[26px] overflow-hidden h-64 shadow-xl">
              <img src={resolveUrl(slots.patio)} alt="Patio dining" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06271D]/60 to-transparent" />
              <div className="absolute bottom-5 left-5 flex items-center gap-2 text-white">
                <PartyPopper size={20} className="text-[var(--gold-light)]" />
                <span className="font-display font-bold text-xl">Your private venue</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-display font-bold text-2xl text-[#0A3B2E] mt-8">Why host here</h3>
            <div className="mt-5 space-y-3">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#0A3B2E]/5 grid place-items-center text-[var(--moss-deep)] shrink-0">
                    <h.icon size={17} />
                  </span>
                  <span className="text-[#16241E]/75 text-[15px]">{h.label}</span>
                </div>
              ))}
            </div>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="events-whatsapp-btn"
              className="btn-shine inline-flex items-center gap-2 mt-7 rounded-full sunset-fill text-white font-body font-bold px-6 py-3.5 text-sm"
            >
              <WhatsApp size={17} /> Chat on WhatsApp
            </a>
          </Reveal>
        </div>

        {/* Right: inquiry form */}
        <div className="lg:col-span-3">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="rounded-[26px] bg-white/70 backdrop-blur-sm border border-white/70 shadow-[0_25px_60px_rgba(10,59,46,0.12)] p-6 md:p-8"
          >
            {done ? (
              <div className="text-center py-12" data-testid="events-success">
                <div className="w-16 h-16 rounded-full sunset-fill grid place-items-center mx-auto mb-5">
                  <CheckCircle2 size={32} className="text-white" />
                </div>
                <h3 className="font-display font-bold text-2xl text-[#0A3B2E]">Inquiry received!</h3>
                <div className="text-[#16241E]/75 mt-4 max-w-md mx-auto space-y-3 text-left sm:text-center text-[15px]">
                  <p>Hello, and thank you for reaching out to The Jackson House! We've received your inquiry and will get back to you as quickly as possible.</p>
                  <p>For the fastest assistance, you're also welcome to text us at <a href="tel:+19562425130" className="font-semibold text-[var(--moss-deep)] underline">956-242-5130</a>.</p>
                  <p>We'd be honored to host your special event at The Jackson House — from birthdays and parties to pool days, girls' nights, showers, and more.</p>
                  <p>We look forward to connecting with you soon!<br /><span className="font-semibold">The Jackson House Team</span></p>
                </div>
                <button
                  onClick={() => setDone(null)}
                  data-testid="events-another-btn"
                  className="mt-7 sunset-fill text-white font-body font-bold px-7 py-3 rounded-full"
                >
                  Send another inquiry
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <PartyPopper size={20} className="text-[var(--moss-deep)]" />
                  <h3 className="font-display font-bold text-xl text-[#0A3B2E]">Tell us about your event</h3>
                </div>
                <p className="text-[#16241E]/55 text-sm mb-5">
                  Share a few details and we'll get back to you with availability &amp; pricing.
                </p>
                <form onSubmit={submit} className="space-y-3.5" data-testid="events-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" required placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="events-name" />
                    <input type="email" required placeholder="Email address *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="events-email" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="tel" required placeholder="Phone number *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="events-phone" />
                    <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className={`${inputCls} bg-white`} data-testid="events-type">
                      {TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#16241E]/50 mb-1 ml-1">Event date *</label>
                      <input type="date" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className={inputCls} data-testid="events-date" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#16241E]/50 mb-1 ml-1">Estimated guests</label>
                      <input type="number" min="1" placeholder="e.g. 25" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className={inputCls} data-testid="events-guests" />
                    </div>
                  </div>
                  <textarea rows={4} placeholder="Tell us about your celebration (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} data-testid="events-message" />
                  <Magnetic strength={0.25}>
                    <button type="submit" disabled={submitting} className="btn-shine w-full flex items-center justify-center gap-2 sunset-fill text-white font-body font-bold py-4 rounded-full disabled:opacity-70" data-testid="events-submit">
                      {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Send Event Inquiry <Send size={17} /></>}
                    </button>
                  </Magnetic>
                  <p className="text-xs text-center text-[#16241E]/50 flex items-center justify-center gap-1.5">
                    <MessageCircle size={13} /> Prefer to chat? Message us on WhatsApp anytime.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox(null)}
            data-testid="events-lightbox"
            className="fixed inset-0 z-[200] bg-[#04140E]/92 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <button
              onClick={() => setLightbox(null)}
              data-testid="events-lightbox-close"
              aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white transition-colors"
            >
              <X size={22} />
            </button>
            <motion.img
              key={lightbox}
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: EASE }}
              src={resolveUrl(EVENT_PHOTOS[lightbox])}
              alt={`Event ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] max-w-[92vw] w-auto rounded-2xl shadow-2xl object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
